import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown } from 'lucide-react';
import { cn } from 'lib/utils';
import { useAdminAuditEvents } from 'hooks/useAdminAuditEvents';
import type { AdminAuditEvent } from 'api/adminAuditApi';
import ChildRowsStatus from './ChildRowsStatus';
import EventRow from './EventRow';
import RealignChildCells from './RealignChildCells';
import { CHILDREN_PAGE, type OpenEvent } from './tableColumns';

/** Realigned tickets of one `sector_realignment` parent (8b), fetched once its row is expanded, paged by « Charger plus ». */
const RealignChildRows = ({ parent, onOpen }: { parent: AdminAuditEvent; onOpen: OpenEvent }) => {
  const { t } = useTranslation();
  const [shown, setShown] = useState(CHILDREN_PAGE);
  const { events, total, isLoading, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } = useAdminAuditEvents({ realignmentId: parent.entityId });

  if (isLoading || isError) return <ChildRowsStatus isError={!isLoading} onRetry={() => refetch()} />;

  const visible = events.slice(0, shown);
  // A realigned ticket's own row has no desk: the drawer (8c) reads the parent's.
  const openChild: OpenEvent = (child, trigger) => onOpen({ ...child, context: { ...child.context, sd: parent.context?.sd } }, trigger);
  const loadMore = () => {
    setShown((n) => n + CHILDREN_PAGE);
    if (shown + CHILDREN_PAGE >= events.length && hasNextPage) fetchNextPage();
  };

  return (
    <>
      {visible.map((child) => (
        <EventRow key={child.id} event={child} onOpen={openChild} dense cells={(link) => <RealignChildCells event={child} link={link} />} className="bg-zinc-50 dark:bg-zinc-950" />
      ))}
      {(events.length > visible.length || hasNextPage) && (
        <div role="row" className="border-b bg-zinc-50 py-3 pl-10 pr-5 dark:bg-zinc-950">
          <div role="cell" className="flex items-center gap-3">
            <button type="button" disabled={isFetchingNextPage} onClick={loadMore} className="inline-flex h-[34px] items-center gap-1.5 rounded-md border border-input bg-card px-3 text-[12.5px] font-medium hover:bg-accent disabled:opacity-50">
              <ArrowDown className={cn('h-3.5 w-3.5', isFetchingNextPage && 'animate-spin')} aria-hidden="true" />
              {t('Load more')}
            </button>
            <span className="text-[12.5px] text-muted-foreground">{t('{{shown}} tickets shown out of {{total}}', { shown: visible.length, total })}</span>
          </div>
        </div>
      )}
    </>
  );
};

export default RealignChildRows;
